import { AxiosError, AxiosResponse } from 'axios'
import { AuthedAxiosRequests } from 'features/auth'

type GcsToken = {
  credentials: string
  bucket: string
  expiry: Date
}

export class PhotoManager {
  entryID: string
  imageNames: string[]
  gcsToken: GcsToken | undefined
  axios: AuthedAxiosRequests
  rolodexURL: string

  constructor(options: {
    entryID: string
    imageNames: string[]
    axios: AuthedAxiosRequests
    rolodexURL: string
  }) {
    this.entryID = options.entryID
    this.imageNames = options.imageNames
    this.axios = options.axios
    this.rolodexURL = options.rolodexURL
  }

  async fetchPhoto(
    imageName: string,
    location: 'thumbnails' | 'raw' | 'sam2' | 'siglip'
  ): Promise<string> {
    await this._refreshToken()

    if (this.gcsToken === undefined) {
      throw new Error('Failed to get GCS credentials')
    }

    const gcsOptions = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.gcsToken.credentials}`,
      },
    }

    let imagePath: string
    switch (location) {
      case 'thumbnails':
        imagePath = `photos/thumbnails/${imageName}`
        break
      case 'raw':
        imagePath = `photos/raw/${imageName}`
        break
      case 'sam2':
        imagePath = `photos/sam2/${imageName}`
        break
      case 'siglip':
        imagePath = `photos/siglip/rust/${imageName}`
        break
      default:
        throw new Error(`Unknown location: ${location}`)
    }

    const sanitizedURL = `${this.entryID}/${imagePath}`.replaceAll('/', '%2F')
    const gcsUrl = `https://storage.googleapis.com/storage/v1/b/${this.gcsToken.bucket.slice(5)}/o/${sanitizedURL}?alt=media`

    const response = await fetch(gcsUrl, gcsOptions)
    if (!response.ok) {
      throw new Error(
        `Failed to fetch image: ${response.status} ${response.statusText}`
      )
    }

    const blob = await response.blob()
    const imageUrl = URL.createObjectURL(blob)
    return imageUrl
  }

  async _refreshToken(): Promise<void> {
    if (
      this.gcsToken !== undefined &&
      Date.now() < this.gcsToken.expiry.getTime()
    ) {
      return Promise.resolve()
    }

    const url = `${this.rolodexURL}/api/v2/files/shortlivedcreds/${this.entryID}?prefix=photos`

    try {
      const res: AxiosResponse = await this.axios.get(url)
      const data = res.data

      if (!data.credentials || !data.bucket || !data.expiry) {
        console.error(`Response poorly formatted`, res.status, res)
        throw new Error('Invalid credentials response')
      }

      this.gcsToken = {
        credentials: data.credentials,
        bucket: data.bucket,
        expiry: new Date(data.expiry + 'Z'),
      }
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.error('Issue grabbing rolodex gcs token', axiosError)
      throw axiosError
    }
  }

  revokeObjectURL(url: string): void {
    URL.revokeObjectURL(url)
  }
}
